<!-- Workspace Modules List with Tabs -->
<template>
  <div class="container-fluid py-4">
    <!-- Header with Tabs and Create Button -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'default' }"
            @click="setActiveTab('default')"
            type="button"
          >
            Default
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'lookup' }"
            @click="setActiveTab('lookup')"
            type="button"
          >
            By Users
          </button>
        </li>
      </ul>
      
      <!-- Create Button - Only show if not By Users tab or if By Users tab has modules -->
      <button
        v-if="activeTab === 'default' || (activeTab === 'lookup' && modules.length > 0)"
        class="btn btn-primary"
        @click="$router.push('/create-workspace-module')"
        :disabled="!canCreate('main')"
      >
        <i class="fa fa-plus me-2"></i>
        Create New
      </button>
    </div>

    <!-- Tab Content -->
    <div class="card">
      <div class="card-header">
        <h5 class="card-title mb-0">
          {{ currentTabTitle }}
        </h5>
      </div>
      
      <div class="card-body">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2">Loading {{ activeTab === 'default' ? 'Default' : 'By Users' }} modules...</p>
        </div>
        
        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger">
          <div class="d-flex align-items-center">
            <i class="fa fa-exclamation-triangle me-2"></i>
            <div class="flex-grow-1">
              <strong>Error:</strong> {{ error }}
            </div>
            <button class="btn btn-sm btn-outline-danger" @click="fetchModules">
              <i class="fa fa-refresh"></i> Retry
            </button>
          </div>
        </div>
        
        <!-- Modules Table -->
        <div v-else-if="modules.length > 0">
          <CommonTable2
            :tableObj="tableConfig"
            :title="''"
            permissionKey="main"
            @edit="handleEdit"
            @delete="handleDelete"
            @view="handleView"
          />
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-5">
          <div class="mb-3">
            <i class="fa fa-puzzle-piece fa-3x text-muted"></i>
          </div>
          <h5 class="text-muted">No {{ activeTab === 'default' ? 'Default' : 'By Users' }} Modules Found</h5>
          <p class="text-muted">
            {{ activeTab === 'default' 
              ? 'No default modules are available.' 
              : 'No By Users modules are available.' 
            }}
          </p>
          <div class="mt-3">
            <button 
              v-if="activeTab === 'default'"
              class="btn btn-primary me-2" 
              @click="$router.push('/create-workspace-module')"
              :disabled="!canCreate('main')"
            >
              <i class="fa fa-plus me-1"></i>
              Create Module
            </button>
            <button class="btn btn-outline-secondary" @click="fetchModules">
              <i class="fa fa-refresh me-1"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      <!-- Summary Footer -->
      <div v-if="modules.length > 0" class="card-footer bg-light">
        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted">
            Showing {{ modules.length }} {{ activeTab === 'default' ? 'default' : 'By Users' }} module{{ modules.length !== 1 ? 's' : '' }}
          </small>
          <button class="btn btn-sm btn-outline-secondary" @click="fetchModules">
            <i class="fa fa-refresh me-1"></i>
            Refresh
          </button>
        </div>
      </div>
    </div>

    <!-- pagination -->
    <div v-if="modules.length > 0" class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4">
      <!-- Rows per page -->
      <div class="d-flex align-items-center gap-2">
        <span class="text-muted">Rows per page:</span>
        <select
          class="form-select form-select-sm rows-select"
          v-model="tablePagination.per_page"
          @change="changeRows"
        >
          <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
            {{ n }}
          </option>
        </select>
      </div>
      <!-- Pagination Buttons -->
      <div class="pagination-mini">
        <!-- Previous -->
        <button
          class="page-btn prev_btn"
          :disabled="!tablePagination.has_prev"
          @click="handlePageChange(tablePagination.current_page - 1)"
        >
          «
        </button>
        <!-- Numbered pages -->
        <button
          v-for="(p, index) in visiblePages"
          :key="index"
          class="page-btn"
          :class="{
            'page-active': p === tablePagination.current_page,
            'disabled-dots': p === '...',
          }"
          :disabled="p === '...'"
          @click="p !== '...' && typeof p === 'number' && handlePageChange(p)"
        >
          {{ p }}
        </button>
        <!-- Next -->
        <button
          class="page-btn next_btn"
          :disabled="!tablePagination.has_next"
          @click="handlePageChange(tablePagination.current_page + 1)"
        >
          »
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import apiService from '@/services/apiService';
// import zeroConfiguration from '@/components/commen/table/zeroConfiguration.vue';
import CommonTable2 from '@/components/commen/table/CommonTable2.vue';
import { usePermissions } from '@/composables/usePermissions';

const { canCreate } = usePermissions();

const router = useRouter();

// Constants
const pageTitle = "Workspace Modules";

// Reactive state
const activeTab = ref<'default' | 'lookup'>('default');
const isLoading = ref(false);
const error = ref<string | null>(null);
const modules = ref<any[]>([]);

// --- Pagination State ---
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Computed properties
const currentTabTitle = computed(() => 
  activeTab.value === 'default' ? 'Default Modules' : 'By Users Modules'
);

// Table configuration for zeroConfiguration component
const tableConfig = computed(() => ({
  data: processedModules.value,
  columns: [
    { name: "Icon", field: "icon", sortable: false },
    { name: "Title", field: "title", sortable: true },
    { name: "Type", field: "module_type", sortable: false },
  ],
  actions: activeTab.value === 'default' ? [true] : [false], // Only show actions for default tab
  loading: isLoading.value,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",
}));

const changeRows = async () => {
  tablePagination.value.current_page = 1;
  await fetchModules(1, tablePagination.value.per_page);
};

const handlePageChange = async (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (page > tablePagination.value.total_pages) return;

  await fetchModules(page, tablePagination.value.per_page);
};

// Process modules data to ensure icons are properly formatted for zeroConfiguration
const processedModules = computed(() => 
  modules.value.map(module => ({
    ...module,
    // Ensure icon field is properly set for zeroConfiguration table
    icon: module.icon || null
  }))
);

// Methods
const setActiveTab = (tab: 'default' | 'lookup') => {
  activeTab.value = tab;
  tablePagination.value.current_page = 1;
  fetchModules(1, tablePagination.value.per_page);
};

const fetchModules = async (page = 1, limit = 10) => {
  isLoading.value = true;
  error.value = null;
  
  try {
    console.log(`Fetching ${activeTab.value} modules...`);
    const isDefault = activeTab.value === 'default';
    const response = await apiService.get('/common/workspace-modules-filter', { 
      is_default: isDefault,
      page,
      limit,
    });
    
    console.log('API Response:', response);
    
    if (Array.isArray(response.data)) {
      // Add module_type field for table display
      modules.value = response.data.map(module => ({
        ...module,
        module_type: activeTab.value === 'default' ? 'Default' : 'By Users'
      }));
      console.log(`Loaded ${modules.value.length} ${activeTab.value} modules`);
    } else {
      throw new Error('Invalid response format');
    }

    // backend gives pagination object
    if (response.pagination) {
      tablePagination.value.current_page = response.pagination.current_page || page;
      tablePagination.value.total_pages = response.pagination.total_pages || 1;
      tablePagination.value.total = response.pagination.total || modules.value.length;
      tablePagination.value.has_prev = (response.pagination.current_page || page) > 1;
      tablePagination.value.has_next =
        (response.pagination.current_page || page) < (response.pagination.total_pages || 1);
    } else {
      // Fallback if no pagination metadata
      tablePagination.value.current_page = page;
      tablePagination.value.total_pages = 1;
      tablePagination.value.total = modules.value.length;
      tablePagination.value.has_prev = false;
      tablePagination.value.has_next = false;
    }
  } catch (err: any) {
    console.error('Error fetching modules:', err);
    error.value = err?.response?.data?.message || err?.message || 'Failed to load modules';
    modules.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Event handlers for zeroConfiguration table
const handleEdit = (module: any) => {
  // Only allow editing for default modules
  if (activeTab.value !== 'default') {
    console.log('Edit action not allowed for By Users modules');
    return;
  }
  
  if (!module._id) return;
  router.push({ 
    name: "create-workspace-module", 
    params: { moduleId: module._id } 
  });
};

const handleView = (module: any) => {
  // Only allow viewing for default modules
  if (activeTab.value !== 'default') {
    console.log('View action not allowed for By Users modules');
    return;
  }
  
  console.log('Viewing module:', module);
  alert(`Module: ${module.title}\nType: ${module.module_type}\nID: ${module._id}`);
};

const handleDelete = async (id: string) => {
  // Only allow deleting for default modules
  if (activeTab.value !== 'default') {
    console.log('Delete action not allowed for By Users modules');
    return;
  }
  
  if (!confirm("Are you sure you want to delete this module?")) return;
  
  try {
    console.log('Deleting module:', id);
    await apiService.delete('/common/workspace-modules', id);
    
    // Refresh the current tab data
    await fetchModules(tablePagination.value.current_page, tablePagination.value.per_page);
    
    // Show success message (you can replace with toast notification)
    console.log('Module deleted successfully');
  } catch (err: any) {
    console.error('Error deleting module:', err);
    alert('Failed to delete module: ' + (err?.message || 'Unknown error'));
  }
};

// Lifecycle
onMounted(() => {
  fetchModules(tablePagination.value.current_page, tablePagination.value.per_page);
});
</script>

<style scoped>
/* Tab styling */
.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
  color: #6c757d;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  background-color: transparent;
  border-color: transparent transparent #35bfbf transparent;
  color: #35bfbf;
  font-weight: 700;
}

.nav-tabs .nav-link:hover {
  border-color: transparent transparent #dee2e6 transparent;
  color: #495057;
}

/* Card styling */
.card-title {
  font-weight: 600;
  color: #2c3e50;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }
}

.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}

.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}

.page-active:hover {
  background: #21c1d6;
}

.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>